import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import FormRequest from '../common/formRequest';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { FiscoService } from 'src/app/app-core/services/fisco.service';
import { UserDataRequestService } from 'src/app/app-core/services/user-data-request.service';
import { getValuesActivity, getValuesGroupActivity } from '../utilities/values-activity';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';


@Component({
  selector: 'app-form-request-semurb',
  templateUrl: './form-request-semurb.component.html',
  styleUrls: ['./form-request-semurb.component.scss'],
})
export class FormRequestSemurbComponent extends FormRequest implements OnInit, OnChanges, OnDestroy {

  @Input() applicantDetails;
  @Input() subject: string;
  private destroy$ = new Subject<void>();

  modalidades: { value: string, label: string, subOptions?: string[] }[] = [
    { value: 'Renovação', label: 'Renovação' },
    {
      value: 'Regularização', label: 'Regularização', subOptions: [
        'Empreendimento ou Atividade sem qualquer licenciamento ambiental',
        'Empreendimento ou Atividade com licença vencida'
      ]
    },
    { value: 'Empreendimento Sem Licença ou Com Licença Vencida', label: 'Empreendimento Sem Licença ou Com Licença Vencida' }
  ];

  gruposAtividade = getValuesGroupActivity();
  atividades = getValuesActivity();

  selectedGroup: string = '';

  constructor(
    public fb: FormBuilder,
    public fiscoService: FiscoService,
    private userDataRequestService: UserDataRequestService,
  ) {
    super(fb, fiscoService);
  }

  ngOnInit(): void {
    this.initForm();
    this.formChanges();
    this.updateFormValidation();
  
    combineLatest([
      this.userDataRequestService.getRepresentingAnotherPerson(),
      this.userDataRequestService.getRequerente()
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([isRepresenting, requerente]) => {
        this.changeDataRequester(isRepresenting, requerente);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.subject && !changes.subject.firstChange) {
      this.updateFormValidation();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateFormValidation(): void {
    const fieldsToEnableDisable = (fields: string[], enable: boolean) => {
      fields.forEach(fieldName => {
        const control = this.form.get(fieldName);
        if (control) {
          if (enable) {
            control.setValidators([Validators.required]);
            control.enable();
          } else {
            control.clearValidators();
            control.disable();
          }
        }
      });
    };

    fieldsToEnableDisable(['modalidade'], !this.shouldHideModality);
    fieldsToEnableDisable(['grupoAtividade', 'atividade'], !this.shouldHideActivity);

    const sectionFields = ['atividadeEmpreendimento', 'responsavel'];
    fieldsToEnableDisable(sectionFields, !this.shouldHideSections);
    fieldsToEnableDisable(['informacoesComplementares'], this.shouldShowComplementaryInfo)

    this.form.updateValueAndValidity();
  }

  get shouldHideSections(): boolean {
    const subjectsToHideFor = [
      'PESSOA JURÍDICA: CADASTRO TÉCNICO MUNICIPAL DE ATIVIDADES E INSTRUMENTOS DE DEFESA AMBIENTAL - CTM/AIDA',
      'PESSOA FÍSICA: CADASTRO TÉCNICO MUNICIPAL DE ATIVIDADES E INSTRUMENTOS DE DEFESA AMBIENTAL - CTM/AIDA',
      'APRESENTAÇÃO DE CONDICIONANTE DE LICENÇA/AUTORIZAÇÃO AMBIENTAL'
    ];
    return subjectsToHideFor.includes(this.subject);
  }

  get shouldHideModality(): boolean {
    const subjectsToHideFor = [
      'PESSOA JURÍDICA: CADASTRO TÉCNICO MUNICIPAL DE ATIVIDADES E INSTRUMENTOS DE DEFESA AMBIENTAL - CTM/AIDA',
      'PESSOA FÍSICA: CADASTRO TÉCNICO MUNICIPAL DE ATIVIDADES E INSTRUMENTOS DE DEFESA AMBIENTAL - CTM/AIDA',
      'AUTORIZAÇÃO PARA USO DE ESPAÇO PÚBLICO',
      'DECLARAÇÃO DE INEXIGIBILIDADE AMBIENTAL',
      'DECLARAÇÃO DE REGULARIDADE ',
      'MUDANÇA DE TITULARIDADE',
      'CARTA DE ANUÊNCIA',
      'APRESENTAÇÃO DE CONDICIONANTE DE LICENÇA/AUTORIZAÇÃO AMBIENTAL',
      'CONSULTA PRÉVIA',
      'ALTERAÇÃO/RETIFICAÇÃO'
    ];
    return subjectsToHideFor.includes(this.subject);
  }

  get shouldHideActivity(): boolean {
    const subjectsToHideFor = [
      'PESSOA JURÍDICA: CADASTRO TÉCNICO MUNICIPAL DE ATIVIDADES E INSTRUMENTOS DE DEFESA AMBIENTAL - CTM/AIDA',
      'PESSOA FÍSICA: CADASTRO TÉCNICO MUNICIPAL DE ATIVIDADES E INSTRUMENTOS DE DEFESA AMBIENTAL - CTM/AIDA',
      'DECLARAÇÃO DE INEXIGIBILIDADE AMBIENTAL',
      'AUTORIZAÇÃO PARA USO DE ESPAÇO PÚBLICO',
      'SOLICITAÇÃO DE LICENÇA AMBIENTAL SIMPLIFICADA',
      'DECLARAÇÃO DE REGULARIDADE ',
      'APRESENTAÇÃO DE CONDICIONANTE DE LICENÇA/AUTORIZAÇÃO AMBIENTAL',
      'CONSULTA PRÉVIA',
      'ALTERAÇÃO/RETIFICAÇÃO'
    ];
    return subjectsToHideFor.includes(this.subject);
  }

  get shouldShowComplementaryInfo(): boolean {
    return this.subject === 'APRESENTAÇÃO DE CONDICIONANTE DE LICENÇA/AUTORIZAÇÃO AMBIENTAL';
  }

  updateEnderecoForm(endereco: any, formGroupName: string) {
    this.form.get(formGroupName).patchValue({
      [`cep`]: endereco.CEP,
      [`uf`]: endereco.uf,
      [`municipio`]: endereco.localidade,
      [`logradouro`]: endereco.logradouro,
      [`numero`]: endereco.numero,
      [`complemento`]: endereco.complemento,
      [`bairro`]: endereco.bairro
    });
  }

  handleSolicitanteEndereco(endereco: any) {
    this.updateEnderecoForm(endereco, 'solicitante');
  }
  
  handleRequerenteEndereco(endereco: any) {
    this.updateEnderecoForm(endereco, 'requerente');
  }

  updateActivityAddress(endereco: any) {
    this.updateEnderecoForm(endereco, 'atividadeEmpreendimento');
  }

  updateResponsibleAddress(endereco: any) {
    this.updateEnderecoForm(endereco, 'responsavel');
  }

  private initForm() {
    this.form = this.fb.group({
      SOL_Descricao: [null, Validators.required],
      modalidade: ['', Validators.required],
      grupoAtividade: ['', Validators.required],
      atividade: ['', Validators.required],

      solicitante: this.fb.group({
        nomeRazao: ['', Validators.required],
        cpfCnpj: ['', Validators.required],
        email: ['', Validators.required],
        telefone: ['', Validators.required],
        cep: ['', Validators.required],
        uf: ['', Validators.required],
        municipio: ['', Validators.required],
        logradouro: ['', Validators.required],
        numero: ['', Validators.required],
        complemento: [''],
        bairro: ['', Validators.required]
      }),

      requerente: this.fb.group({
        nomeRazao: ['', Validators.required],
        cpfCnpj: ['', Validators.required],
        email: ['', Validators.required],
        telefone: ['', Validators.required],
        cep: ['', Validators.required],
        uf: ['', Validators.required],
        municipio: ['', Validators.required],
        logradouro: ['', Validators.required],
        numero: ['', Validators.required],
        complemento: [''],
        bairro: ['', Validators.required]
      }),

      atividadeEmpreendimento: this.fb.group({
        nomeEmpreendimento: ['', Validators.required],
        cep: ['', Validators.required],
        uf: ['', Validators.required],
        municipio: ['', Validators.required],
        logradouro: ['', Validators.required],
        numero: ['', Validators.required],
        complemento: [''],
        bairro: ['', Validators.required],
        areaConstruida: [null, Validators.required],
        numeroFuncionarios: [null, Validators.required],
        licencaAnterior: ['', Validators.required],
        supressaoVegetal: [null, Validators.required]
      }),

      responsavel: this.fb.group({
        nome: ['', Validators.required],
        cpf: ['', Validators.required],
        email: ['', Validators.required],
        telefone: ['', Validators.required],
        cep: ['', Validators.required],
        uf: ['', Validators.required],
        municipio: ['', Validators.required],
        logradouro: ['', Validators.required],
        numero: ['', Validators.required],
        complemento: [''],
        bairro: ['', Validators.required]
      }),

      consultoria: this.fb.group({
        nome: [''],
        cpfCnpj: [''],
        email: [''],
        telefone: ['']
      }),

      informacoesComplementares: this.fb.group({
        tipologiaLicenca: ['', Validators.required],
        processoVinculado: ['', Validators.required]
      }),

      formVariants: this.fb.group({
        termoAceito: [false, Validators.requiredTrue],
      })
    });
  }

  onGroupChange(selectedValue: string) {
    this.selectedGroup = selectedValue;
    this.atividades = this.getFilteredActivities(selectedValue);
  }

  private getFilteredActivities(groupValue: string) {
    return getValuesActivity().filter(activity => activity.grupo === groupValue);
  }

  public changeDataRequester(isRepresenting: boolean = false, requerente?: any) {
    if (isRepresenting) {
      this.form?.patchValue({
        solicitante: {
          nomeRazao: '',
          cpfCnpj: '',
          email: '',
          telefone: ''
        },
        requerente: {
          nomeRazao: requerente?.SOLR_NomeRazaoSocial ?? '',
          cpfCnpj: requerente?.SOLR_CpfCnpj ?? '',
          email: requerente?.SOLR_Email ?? '',
          telefone: requerente?.SOLR_Telefone ?? ''
        }
      }, { emitEvent: false });

      this.enabledFields([
        'solicitante.nomeRazao',
        'solicitante.cpfCnpj',
        'solicitante.email',
        'solicitante.telefone'
      ]);

      this.disabledFields([
        'requerente.nomeRazao',
        'requerente.cpfCnpj',
        'requerente.email',
        'requerente.telefone'
      ]);

      this.form.updateValueAndValidity({ emitEvent: false });
      return;
    }

    const sourceData = {
      nomeRazao: this.applicantDetails?.SOLR_NomeRazaoSocial ?? requerente?.SOLR_NomeRazaoSocial ?? '',
      cpfCnpj: this.applicantDetails?.SOLR_CpfCnpj ?? requerente?.SOLR_CpfCnpj ?? '',
      email: this.applicantDetails?.SOLR_Email ?? requerente?.SOLR_Email ?? '',
      telefone: this.applicantDetails?.SOLR_Telefone ?? requerente?.SOLR_Telefone ?? ''
    };

    this.form?.patchValue({
      solicitante: sourceData,
      requerente: {
        nomeRazao: '',
        cpfCnpj: '',
        email: '',
        telefone: ''
      }
    }, { emitEvent: false });

    this.disabledFields([
      'solicitante.nomeRazao',
      'solicitante.cpfCnpj',
      'solicitante.email',
      'solicitante.telefone'
    ]);

    this.enabledFields([
      'requerente.nomeRazao',
      'requerente.cpfCnpj',
      'requerente.email',
      'requerente.telefone'
    ]);

    this.form.updateValueAndValidity({ emitEvent: false });
  }
}

